import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code2, Play, Target, Users, Star, ChevronRight, CheckCircle, ArrowRight, PlayCircle, Github, Linkedin, Heart, Lightbulb, TrendingUp, Shield, Award, BookOpen, Zap } from 'lucide-react';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';

const Homepage = () => {
  const navigate = useNavigate();
  const [counters, setCounters] = useState({ users: 0, missions: 0, success: 0 });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [selectedMilestone, setSelectedMilestone] = useState(0);

  // Enhanced authentication state with proper session management
  const [currentUser, setCurrentUser] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  
  // Enhanced authentication check with persistent login
  useEffect(() => {
    const checkAuthenticationStatus = async () => {
      setIsAuthenticating(true);
      
      try {
        // Check for auth token first
        const authToken = localStorage.getItem('authToken');
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const userData = localStorage.getItem('userData');
        
        if (authToken && userData) {
          // Validate session expiry if remember me is not enabled
          const loginTime = localStorage.getItem('loginTime');
          const currentTime = Date.now();
          const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
          
          if (rememberMe || (loginTime && (currentTime - parseInt(loginTime)) < sessionDuration)) {
            // Valid session, restore user
            try {
              const parsedUser = JSON.parse(userData);
              setCurrentUser(parsedUser);
            } catch (error) {
              console.error('Error parsing user data:', error);
              handleLogout(); // Clear invalid data
            }
          } else {
            // Session expired, clear data
            handleLogout();
          }
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        handleLogout();
      } finally {
        setIsAuthenticating(false);
      }
    };

    checkAuthenticationStatus();
  }, []);

  // Enhanced logout with proper cleanup
  const handleLogout = () => {
    // Clear all authentication data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('loginTime');
    localStorage.removeItem('currentUser'); // Clean up old storage
    
    setCurrentUser(null);
  };

  // Animated counters
  useEffect(() => {
    const targetValues = { users: 2500, missions: 150, success: 94 };
    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      
      setCounters({
        users: Math.floor(targetValues?.users * progress),
        missions: Math.floor(targetValues?.missions * progress),
        success: Math.floor(targetValues?.success * progress)
      });
      
      if (step >= steps) {
        clearInterval(timer);
        setCounters(targetValues);
      }
    }, stepDuration);
    
    return () => clearInterval(timer);
  }, []);

  // Add testimonials declaration before it's used
  const testimonials = [
    {
      name: "Sarah Chen",
      role: "QA Lead at Google",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=64&h=64&fit=crop&crop=face",
      content: "Testing Academy transformed my career. From manual testing basics to advanced automation frameworks, the comprehensive approach gave me confidence to ace my senior QA role at Google.",
      linkedin: "https://linkedin.com/in/sarahchen"
    },
    {
      name: "Marcus Rodriguez",
      role: "Senior Test Engineer at Microsoft",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face",
      content: "The end-to-end testing curriculum is incredible. Manual testing, automation, API testing - everything I needed to become a complete testing professional in one platform.",
      linkedin: "https://linkedin.com/in/marcusrodriguez"
    },
    {
      name: "Priya Patel",
      role: "Test Automation Lead at Amazon",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face",
      content: "From zero knowledge to landing my dream job in 4 months. The mission-based approach covering all testing disciplines kept me motivated throughout my learning journey.",
      linkedin: "https://linkedin.com/in/priyapatel"
    }
  ];

  // Testimonial carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // About section data
  const teamMembers = [
    {
      name: "Alex Chen",
      role: "Founder & Lead Instructor",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face",
      bio: "Former Senior QA Engineer at Google with 8+ years in automation testing. Passionate about making complex testing concepts accessible to everyone.",
      expertise: ["Selenium", "Test Architecture", "CI/CD", "Mentoring"],
      linkedin: "https://linkedin.com/in/alexchen",
      github: "https://github.com/alexchen"
    },
    {
      name: "Sarah Rodriguez",
      role: "Platform Architect",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=300&h=300&fit=crop&crop=face",
      bio: "Full-stack developer with expertise in cloud infrastructure and educational technology. Previously at Microsoft Azure team.",
      expertise: ["Cloud Architecture", "Docker", "React", "Python"],
      linkedin: "https://linkedin.com/in/sarahrodriguez",
      github: "https://github.com/sarahrodriguez"
    },
    {
      name: "Michael Patel",
      role: "Curriculum Designer",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face",
      bio: "Educational specialist with a background in instructional design and gamified learning experiences. Former Udemy course creator.",
      expertise: ["Learning Design", "Gamification", "Content Strategy", "User Experience"],
      linkedin: "https://linkedin.com/in/michaelpatel",
      github: "https://github.com/michaelpatel"
    }
  ];

  const milestones = [
    {
      date: "January 2023",
      title: "Project Launch",
      description: "Initial concept and market research for hands-on automation testing education.",
      icon: <Lightbulb className="w-6 h-6" />,
      stats: { users: 0, missions: 0, features: 1 }
    },
    {
      date: "March 2023",
      title: "MVP Development",
      description: "Built the core platform with basic code editor and browser execution environment.",
      icon: <Code2 className="w-6 h-6" />,
      stats: { users: 50, missions: 10, features: 5 }
    },
    {
      date: "June 2023",
      title: "Beta Launch",
      description: "Released to a select group of beta testers and gathered initial feedback.",
      icon: <Users className="w-6 h-6" />,
      stats: { users: 250, missions: 25, features: 12 }
    },
    {
      date: "September 2023",
      title: "Gamification Update",
      description: "Added mission-based progression system with achievements and XP rewards.",
      icon: <Award className="w-6 h-6" />,
      stats: { users: 800, missions: 50, features: 20 }
    },
    {
      date: "December 2023",
      title: "Interview Prep Module",
      description: "Launched comprehensive interview preparation with real-world scenarios.",
      icon: <Target className="w-6 h-6" />,
      stats: { users: 1500, missions: 85, features: 30 }
    },
    {
      date: "March 2024",
      title: "Major Platform Upgrade",
      description: "Enhanced cloud infrastructure for better performance and scalability.",
      icon: <Zap className="w-6 h-6" />,
      stats: { users: 2500, missions: 150, features: 45 }
    }
  ];

  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Accessibility First",
      description: "We believe quality education should be accessible to everyone, regardless of background or financial situation."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Learning by Doing",
      description: "Hands-on practice with real tools and environments provides the most effective learning experience."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community Driven",
      description: "Our platform grows stronger through community feedback, contributions, and shared knowledge."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Quality Focused",
      description: "Every mission and feature is carefully crafted to provide maximum educational value and practical relevance."
    }
  ];

  const techStack = [
    { name: "Docker", description: "Secure, isolated execution environments" },
    { name: "React", description: "Modern, responsive user interface" },
    { name: "Python", description: "Powerful backend automation engine" },
    { name: "Selenium", description: "Industry-standard web automation" },
    { name: "AWS", description: "Scalable cloud infrastructure" },
    { name: "MongoDB", description: "Flexible data storage solution" }
  ];

  const achievements = [
    { value: "2,500+", label: "Active Learners", icon: <Users className="w-6 h-6" /> },
    { value: "150+", label: "Practice Missions", icon: <Target className="w-6 h-6" /> },
    { value: "94%", label: "Success Rate", icon: <TrendingUp className="w-6 h-6" /> },
    { value: "500+", label: "Career Transitions", icon: <Award className="w-6 h-6" /> }
  ];

  const features = [
    {
      icon: <Code2 className="w-8 h-8" />,
      title: "Comprehensive Testing Curriculum",
      description: "Master manual testing, automation frameworks, API testing, and industry-standard testing tools in one integrated learning platform."
    },
    {
      icon: <Play className="w-8 h-8" />,
      title: "Real-World Practice Environment",
      description: "Practice on actual applications with live feedback, test case execution, and detailed reporting across all testing disciplines."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Mission-Based Learning Path",
      description: "Progress through carefully crafted missions covering manual testing, automation, API testing, and frameworks with gamified achievements."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Industry-Ready Preparation",
      description: "Practice with real-world scenarios and technical questions commonly asked in testing interviews across all specializations."
    }
  ];

  const advantages = [
    "100% Free Forever",
    "Complete Testing Education",
    "Manual & Automation Testing",
    "API Testing Mastery",
    "Testing Frameworks",
    "Hands-on Practice Environment"
  ];

  const steps = [
    {
      number: "01",
      title: "Sign Up Free",
      description: "Create your account and access the complete testing education platform instantly."
    },
    {
      number: "02", 
      title: "Choose Your Path",
      description: "Select from manual testing, automation, API testing, or comprehensive full-stack testing curriculum."
    },
    {
      number: "03",
      title: "Practice & Execute",
      description: "Work on real projects, write test cases, automation scripts, and API tests in our cloud environment."
    },
    {
      number: "04",
      title: "Master & Certify",
      description: "Complete missions across all testing disciplines and apply your skills to real-world projects."
    }
  ];

  const companies = [
    { name: "Google", employees: 45 },
    { name: "Microsoft", employees: 38 },
    { name: "Amazon", employees: 52 },
    { name: "Meta", employees: 29 },
    { name: "Netflix", employees: 18 },
    { name: "Tesla", employees: 15 }
  ];

  // Show loading state while checking authentication
  if (isAuthenticating) {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Enhanced Header with proper authentication state */}
      <Header user={currentUser} onLogout={handleLogout} />
      {/* Hero Section */}
      <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                Master Testing
                <span className="block bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                  Through Practice
                </span>
              </h1>
              
              <p className="text-xl text-muted-foreground leading-relaxed">
                We are teaching complete testing - not only Selenium but Manual Testing, Automation Testing, API Testing and Testing Frameworks. 
                From complete beginner to industry-ready professional.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                {currentUser ? (
                  <Button 
                    size="lg" 
                    className="px-8 py-4 text-lg"
                    onClick={() => navigate('/user-profile-dashboard')}
                  >
                    Continue Learning
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <Button 
                    size="lg" 
                    className="px-8 py-4 text-lg"
                    onClick={() => navigate('/user-registration')}
                  >
                    Start Learning Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="px-8 py-4 text-lg"
                  onClick={() => navigate('/roadmaps')}
                >
                  <PlayCircle className="mr-2 w-5 h-5" />
                  View Roadmap
                </Button>
              </div>

              {/* Enhanced authentication status indicator */}
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>100% Free Forever</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span>No Credit Card Required</span>
                </div>
                {currentUser && (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span>Logged in as {currentUser?.username || currentUser?.email}</span>
                  </div>
                )}
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-card border border-border rounded-xl p-6 shadow-elevation-3">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex space-x-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                  <span className="text-sm text-muted-foreground">testing_practice.py</span>
                </div>
                
                <div className="space-y-3 font-mono text-sm">
                  <div className="text-blue-400"># Manual Testing</div>
                  <div className="text-green-400"># Mission: Test Case Design</div>
                  <div className="text-yellow-400"># API Testing with requests</div>
                  <div className="text-pink-400">import requests, selenium</div>
                  <div className="text-orange-400">response = api.test("/login")</div>
                  <div className="text-purple-400">driver.find_element(By.ID, "test")</div>
                  
                  <div className="mt-4 p-3 bg-green-900/20 border border-green-500/20 rounded">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      <span className="text-green-400 text-xs">All Tests Passed! +75 XP</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* What is Testing Academy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-8">What is Testing Academy?</h2>
          <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
            <p>
              Testing Academy is the most comprehensive hands-on learning platform for complete testing education. 
              We teach not only Selenium automation but the full spectrum - Manual Testing, Automation Testing, 
              API Testing, and Testing Frameworks in an integrated, practical approach.
            </p>
            <p>
              With mission-based progression, gamified achievements, and real-world practice environments, 
              you'll gain practical experience across all testing disciplines. From complete testing novice to 
              industry-ready professional, our platform guides you through every aspect of modern software testing.
            </p>
          </div>
        </div>
      </section>
      {/* Key Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Why Choose Testing Academy?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the most effective way to learn complete testing with hands-on practice and real-world scenarios.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features?.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 hover:shadow-elevation-2 transition-all duration-300 group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  {React.cloneElement(feature?.icon, { className: "w-8 h-8 text-white" })}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature?.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{feature?.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Advantages */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold mb-8">Platform Advantages</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {advantages?.map((advantage, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-lg">{advantage}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="bg-card border border-border rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-6">Real-time Execution Environment</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg">
                    <span>Browser Instance</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-green-500 text-sm">Active</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg">
                    <span>Docker Container</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                      <span className="text-blue-500 text-sm">Running</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-muted/10 rounded-lg">
                    <span>Code Execution</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                      <span className="text-purple-500 text-sm">Ready</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Learning Roadmap Preview - Fixed Arrow Overlapping */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-8">Complete Testing Learning Path</h2>
          <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
            Follow our comprehensive curriculum designed to take you from testing fundamentals to advanced frameworks and automation mastery.
          </p>

          <div className="relative">
            {/* Fixed roadmap layout with proper spacing */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mb-12">
              {[
                { level: "Manual Testing", missions: 20, color: "green" },
                { level: "Automation", missions: 25, color: "blue" },
                { level: "API Testing", missions: 18, color: "purple" },
                { level: "Frameworks", missions: 15, color: "orange" }
              ]?.map((section, index) => (
                <motion.div
                  key={index}
                  className={`relative p-6 rounded-2xl border-2 ${
                    section?.color === 'green' ? 'border-green-500 bg-green-500/10' :
                    section?.color === 'blue' ? 'border-blue-500 bg-blue-500/10' :
                    section?.color === 'purple'? 'border-purple-500 bg-purple-500/10' : 'border-orange-500 bg-orange-500/10'
                  }`}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h3 className="text-xl font-bold mb-2">{section?.level}</h3>
                  <p className="text-sm text-muted-foreground">{section?.missions} Missions</p>
                  <div className={`absolute -top-3 -right-3 w-6 h-6 rounded-full ${
                    section?.color === 'green' ? 'bg-green-500' :
                    section?.color === 'blue' ? 'bg-blue-500' :
                    section?.color === 'purple'? 'bg-purple-500' : 'bg-orange-500'
                  } flex items-center justify-center`}>
                    <Star className="w-3 h-3 text-white" />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Fixed arrow positioning - only show on larger screens where stages are in a row */}
            <div className="hidden lg:flex absolute top-1/2 left-0 right-0 -translate-y-1/2 justify-between pointer-events-none z-0">
              <ChevronRight className="absolute left-1/4 transform -translate-x-1/2 text-muted-foreground/50 w-6 h-6" />
              <ChevronRight className="absolute left-1/2 transform -translate-x-1/2 text-muted-foreground/50 w-6 h-6" />
              <ChevronRight className="absolute left-3/4 transform -translate-x-1/2 text-muted-foreground/50 w-6 h-6" />
            </div>

            <Button size="lg" onClick={() => navigate('/roadmaps')}>
              Explore Full Roadmap
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
      {/* Statistics */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl lg:text-5xl font-bold text-blue-500 mb-2">
                {counters?.users?.toLocaleString()}+
              </div>
              <div className="text-lg text-muted-foreground">Active Learners</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl lg:text-5xl font-bold text-purple-500 mb-2">
                {counters?.missions}+
              </div>
              <div className="text-lg text-muted-foreground">Practice Missions</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl lg:text-5xl font-bold text-green-500 mb-2">
                {counters?.success}%
              </div>
              <div className="text-lg text-muted-foreground">Success Rate</div>
            </motion.div>
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Start your comprehensive testing journey with our simple, effective learning process covering all testing disciplines.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps?.map((step, index) => (
              <motion.div
                key={index}
                className="text-center space-y-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                  {step?.number}
                </div>
                <h3 className="text-xl font-bold">{step?.title}</h3>
                <p className="text-muted-foreground">{step?.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">Success Stories</h2>
          <p className="text-lg text-muted-foreground mb-12">
            Hear from testing professionals who transformed their careers with Testing Academy's comprehensive approach.
          </p>

          <motion.div
            key={currentTestimonial}
            className="bg-card border border-border rounded-xl p-8 shadow-elevation-2"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col md:flex-row items-center space-y-6 md:space-y-0 md:space-x-6">
              <img
                src={testimonials?.[currentTestimonial]?.avatar}
                alt={testimonials?.[currentTestimonial]?.name}
                className="w-20 h-20 rounded-full object-cover"
              />
              <div className="text-left flex-1">
                <blockquote className="text-lg mb-4">
                  "{testimonials?.[currentTestimonial]?.content}"
                </blockquote>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">{testimonials?.[currentTestimonial]?.name}</div>
                    <div className="text-muted-foreground text-sm">{testimonials?.[currentTestimonial]?.role}</div>
                  </div>
                  <a
                    href={testimonials?.[currentTestimonial]?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <div className="flex justify-center space-x-2 mt-8">
            {testimonials?.map((_, index) => (
              <button
                key={index}
                className={`w-3 h-3 rounded-full transition-colors ${
                  index === currentTestimonial ? 'bg-blue-500' : 'bg-muted'
                }`}
                onClick={() => setCurrentTestimonial(index)}
              />
            ))}
          </div>
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-12">Trusted by Developers at Top Companies</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {companies?.map((company, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-lg font-semibold mb-1">{company?.name}</div>
                <div className="text-sm text-muted-foreground">{company?.employees} employees</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* About Section - Integrated into Homepage */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/5">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              About 
              <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                {" "}Testing Academy
              </span>
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Empowering developers worldwide to master web automation testing through 
              practical, hands-on learning experiences that bridge the gap between 
              education and real-world application.
            </p>
          </motion.div>
        </div>

        {/* Mission Statement */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-bold mb-6">Our Mission</h3>
              <div className="space-y-4 text-lg text-muted-foreground">
                <p>
                  We're on a mission to democratize automation testing education by providing 
                  a comprehensive, hands-on learning platform that bridges the gap between 
                  theoretical knowledge and practical industry skills.
                </p>
                <p>
                  Our platform empowers developers at all skill levels to master Selenium 
                  automation through real-world missions, gamified progression, and 
                  immediate feedback in a safe, cloud-based environment.
                </p>
              </div>
              
              <div className="mt-8">
                <h4 className="text-xl font-semibold mb-4">Our Vision</h4>
                <p className="text-muted-foreground">
                  To become the world's most trusted platform for practical automation 
                  testing education, enabling millions of developers to advance their 
                  careers and build better, more reliable web applications.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-6"
            >
              {achievements?.map((achievement, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-6 text-center">
                  <div className="flex justify-center mb-3 text-blue-500">
                    {achievement?.icon}
                  </div>
                  <div className="text-3xl font-bold mb-2">{achievement?.value}</div>
                  <div className="text-sm text-muted-foreground">{achievement?.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Team Section */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Meet Our Team</h3>
            <p className="text-lg text-muted-foreground">
              Passionate educators and technologists dedicated to transforming how 
              automation testing is learned and practiced.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers?.map((member, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <img
                  src={member?.avatar}
                  alt={member?.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h4 className="text-xl font-bold mb-2">{member?.name}</h4>
                <p className="text-blue-500 font-semibold mb-4">{member?.role}</p>
                <p className="text-muted-foreground text-sm mb-4">{member?.bio}</p>
                
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  {member?.expertise?.map((skill, skillIndex) => (
                    <span
                      key={skillIndex}
                      className="px-2 py-1 bg-muted text-xs rounded-full"
                    >
                      {skill}
                    </span>
                  ))}
                </div>

                <div className="flex justify-center space-x-3">
                  <a
                    href={member?.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-400 transition-colors"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href={member?.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Github className="w-5 h-5" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Our Journey Timeline */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Our Journey</h3>
            <p className="text-lg text-muted-foreground">
              From concept to comprehensive platform - explore the key milestones 
              that shaped Testing Academy.
            </p>
          </div>

          <div className="relative">
            {/* Desktop Timeline */}
            <div className="hidden lg:block">
              <div className="flex justify-between mb-8">
                {milestones?.map((milestone, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedMilestone(index)}
                    className={`flex flex-col items-center p-4 rounded-lg transition-all ${
                      selectedMilestone === index
                        ? 'bg-blue-500/10 border-2 border-blue-500' :'bg-card border border-border hover:border-blue-500/50'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${
                      selectedMilestone === index
                        ? 'bg-blue-500 text-white' :'bg-muted'
                    }`}>
                      {milestone?.icon}
                    </div>
                    <div className="text-sm text-muted-foreground mb-1">{milestone?.date}</div>
                    <div className="font-semibold text-center">{milestone?.title}</div>
                  </button>
                ))}
              </div>

              <motion.div
                key={selectedMilestone}
                className="bg-card border border-border rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    {React.cloneElement(milestones?.[selectedMilestone]?.icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-bold mb-2">{milestones?.[selectedMilestone]?.title}</h4>
                    <p className="text-muted-foreground mb-6">{milestones?.[selectedMilestone]?.description}</p>
                    
                    <div className="grid grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-500">{milestones?.[selectedMilestone]?.stats?.users?.toLocaleString()}</div>
                        <div className="text-sm text-muted-foreground">Users</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-500">{milestones?.[selectedMilestone]?.stats?.missions}</div>
                        <div className="text-sm text-muted-foreground">Missions</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-500">{milestones?.[selectedMilestone]?.stats?.features}</div>
                        <div className="text-sm text-muted-foreground">Features</div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Mobile Timeline */}
            <div className="lg:hidden space-y-8">
              {milestones?.map((milestone, index) => (
                <motion.div
                  key={index}
                  className="flex space-x-6"
                  initial={{ opacity: 0, x: -50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      {React.cloneElement(milestone?.icon, { className: "w-6 h-6 text-white" })}
                    </div>
                    {index < milestones?.length - 1 && (
                      <div className="w-px h-16 bg-border mt-4"></div>
                    )}
                  </div>
                  <div className="flex-1 pb-8">
                    <div className="text-sm text-muted-foreground mb-1">{milestone?.date}</div>
                    <h4 className="text-xl font-bold mb-2">{milestone?.title}</h4>
                    <p className="text-muted-foreground">{milestone?.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Values */}
        <div className="max-w-6xl mx-auto mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Our Values</h3>
            <p className="text-lg text-muted-foreground">
              The principles that guide our mission to transform automation testing education.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {values?.map((value, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    {React.cloneElement(value?.icon, { className: "w-8 h-8 text-white" })}
                  </div>
                  <div>
                    <h4 className="text-xl font-bold mb-3">{value?.title}</h4>
                    <p className="text-muted-foreground">{value?.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Technology Stack */}
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">Our Technology Stack</h3>
            <p className="text-lg text-muted-foreground">
              Built with modern, reliable technologies to ensure the best learning experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {techStack?.map((tech, index) => (
              <motion.div
                key={index}
                className="bg-card border border-border rounded-xl p-6 text-center hover:shadow-elevation-2 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="text-lg font-bold mb-2">{tech?.name}</h4>
                <p className="text-muted-foreground text-sm">{tech?.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      {/* Footer - Updated with About link removed */}
      <footer className="bg-card border-t border-border py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold">Testing Academy</span>
              </div>
              <p className="text-muted-foreground">
                Master complete testing education - Manual, Automation, API Testing, and Frameworks through hands-on practice.
              </p>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Learning</h3>
              <div className="space-y-3">
                <Link to="/roadmaps" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Roadmaps
                </Link>
                <Link to="/tutorials" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Tutorials
                </Link>
                <Link to="/challenges" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Challenges
                </Link>
                <Link to="/interview-prep" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Interview Prep
                </Link>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <div className="space-y-3">
                <a href="#" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Privacy Policy
                </a>
                <a href="#" className="block text-muted-foreground hover:text-foreground transition-colors">
                  Terms of Service
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Contact Us</h3>
              <form className="space-y-3" onSubmit={(e) => { e?.preventDefault(); alert('Thank you for your message! We\'ll get back to you soon.'); }}>
                <input
                  type="email"
                  placeholder="Your email"
                  required
                  className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input
                  type="text"
                  placeholder="Subject"
                  required
                  className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <textarea
                  placeholder="Your comments or queries..."
                  required
                  rows="3"
                  className="w-full px-3 py-2 text-sm border border-border rounded bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-3 rounded transition-colors"
                >
                  Send Message
                </button>
              </form>
              
              <div className="mt-4 space-y-2">
                <a href="https://github.com" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
                  <Github className="w-4 h-4" />
                  <span>GitHub</span>
                </a>
                <a href="https://linkedin.com" className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors text-sm">
                  <Linkedin className="w-4 h-4" />
                  <span>LinkedIn</span>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border mt-12 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Testing Academy. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;